/**
 * @fileoverview Dashboard activity feed / data list with search and sort
 * @module ctl-activity-feed
 * @version 1.0.0
 *
 * @description
 * A searchable and sortable data list for dashboards. Suitable for
 * activity feeds, recent customer lists, newly available units, etc.
 * Supports pagination, custom column definitions, status badges, and
 * configurable row click events.
 *
 * @example
 * <ctl-activity-feed
 *   title="Recent Orders"
 *   columns='[{"id":"order","label":"Order","sortable":true},{"id":"customer","label":"Customer","sortable":true},{"id":"amount","label":"Amount","align":"right"},{"id":"status","label":"Status","type":"badge"}]'
 *   rows='[{"id":"1","order":"#10234","customer":"Alice Smith","amount":"$240.00","status":"fulfilled","status_intent":"success"},{"id":"2","order":"#10233","customer":"Bob Lee","amount":"$85.50","status":"pending","status_intent":"warning"}]'
 *   searchable
 *   page-size="10">
 * </ctl-activity-feed>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import "../foundation/ctl-skeleton.js";
import "../foundation/ctl-badge.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
  }

  .head {
    padding: var(--ctl-space-3) var(--ctl-space-4);
    border-bottom: 1px solid var(--ctl-color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .title {
    font-size: var(--ctl-font-size-lg);
    font-weight: var(--ctl-font-weight-heavy);
    line-height: 1.2;
    flex: 1;
    min-width: 0;
  }

  .search-wrap {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    background: var(--ctl-color-bg);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-1-5) var(--ctl-space-2-5);
    flex-shrink: 0;
    max-width: 220px;
    min-width: 140px;
    transition: border-color var(--ctl-transition-fast, 150ms);
  }

  .search-wrap:focus-within {
    border-color: var(--ctl-color-brand);
    outline: 2px solid color-mix(in srgb, var(--ctl-color-brand) 25%, transparent);
    outline-offset: 0;
  }

  .search-icon {
    color: var(--ctl-color-muted);
    flex-shrink: 0;
    font-size: var(--ctl-font-size-md);
  }

  .search-input {
    border: none;
    background: transparent;
    outline: none;
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-text);
    width: 100%;
    font-family: inherit;
  }

  .search-input::placeholder { color: var(--ctl-color-muted); }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
  }

  thead th {
    background: var(--ctl-color-surface);
    border-bottom: 1px solid var(--ctl-color-border);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    text-align: left;
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  th.right, td.right { text-align: right; }
  th.center, td.center { text-align: center; }

  .sort-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .sort-btn:hover { color: var(--ctl-color-text); }
  .sort-btn[aria-sort="ascending"], .sort-btn[aria-sort="descending"] { color: var(--ctl-color-brand); }

  .sort-icon { font-size: var(--ctl-font-size-2xs); opacity: 0.5; }
  .sort-btn[aria-sort="ascending"] .sort-icon,
  .sort-btn[aria-sort="descending"] .sort-icon { opacity: 1; }

  tbody tr {
    background: var(--ctl-color-surface);
    transition: background var(--ctl-transition-fast, 150ms);
  }

  tbody tr:hover {
    background: color-mix(in srgb, var(--ctl-color-brand) 5%, var(--ctl-color-surface));
  }

  tbody tr[data-clickable] { cursor: pointer; }

  tbody td {
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    font-size: var(--ctl-font-size-base);
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: none; }

  .cell {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
  }

  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: color-mix(in srgb, var(--ctl-color-brand) 15%, var(--ctl-color-surface));
    color: var(--ctl-color-brand);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    flex-shrink: 0;
  }

  .subtext {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    margin-top: 1px;
  }

  .empty-row td {
    text-align: center;
    padding: var(--ctl-space-6);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-base);
  }

  .footer {
    padding: var(--ctl-space-2-5) var(--ctl-space-4);
    border-top: 1px solid var(--ctl-color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .footer:empty { display: none; }

  .count {
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
  }

  .pages {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
  }

  .page-btn {
    all: unset;
    cursor: pointer;
    padding: var(--ctl-space-1) var(--ctl-space-2-5);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-text);
    line-height: 1;
    min-width: 28px;
    text-align: center;
    transition: background var(--ctl-transition-fast, 150ms);
  }

  .page-btn:hover:not([disabled]) { background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface)); }
  .page-btn[aria-current="page"] {
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand);
    border-color: var(--ctl-color-brand);
  }
  .page-btn[disabled] { opacity: 0.4; cursor: default; }

  :host([loading]) .real { display: none; }
  .loading { display: none; }
  :host([loading]) .loading { display: block; }

  .loading-rows {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2-5);
    padding: var(--ctl-space-3) var(--ctl-space-4);
  }
</style>

<div class="card" part="card">
  <div class="head" part="head">
    <div class="title" id="title"></div>
    <div class="search-wrap" id="searchWrap" hidden>
      <span class="search-icon" aria-hidden="true">⌕</span>
      <input class="search-input" id="searchInput" type="search" placeholder="Search…" aria-label="Search"/>
    </div>
  </div>

  <div class="real">
    <div class="table-wrap" part="table-wrap">
      <table id="table" role="grid">
        <thead id="thead"></thead>
        <tbody id="tbody"></tbody>
      </table>
    </div>
    <div class="footer" id="footer" part="footer"></div>
  </div>

  <div class="loading" part="loading" aria-hidden="true">
    <div class="loading-rows">
      <ctl-skeleton variant="rect" height="14px"></ctl-skeleton>
      <ctl-skeleton variant="rect" height="14px" width="90%"></ctl-skeleton>
      <ctl-skeleton variant="rect" height="14px" width="80%"></ctl-skeleton>
      <ctl-skeleton variant="rect" height="14px" width="85%"></ctl-skeleton>
      <ctl-skeleton variant="rect" height="14px" width="70%"></ctl-skeleton>
    </div>
  </div>
</div>
`;

const BADGE_INTENT_MAP = {
  success: "success",
  fulfilled: "success",
  active: "success",
  complete: "success",
  paid: "success",
  pending: "warning",
  processing: "warning",
  review: "warning",
  warning: "warning",
  failed: "danger",
  cancelled: "danger",
  error: "danger",
  refunded: "danger",
  info: "info",
  neutral: "neutral",
  default: "neutral"
};

function inferIntent(status) {
  const key = (status || "").toLowerCase();
  return BADGE_INTENT_MAP[key] || "neutral";
}

export class CtlActivityFeed extends HTMLElement {
  static get observedAttributes() {
    return ["title", "columns", "rows", "searchable", "loading", "page-size", "empty-label"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._theadEl = this.shadowRoot.getElementById("thead");
    this._tbodyEl = this.shadowRoot.getElementById("tbody");
    this._footerEl = this.shadowRoot.getElementById("footer");
    this._searchWrap = this.shadowRoot.getElementById("searchWrap");
    this._searchInput = this.shadowRoot.getElementById("searchInput");

    this._columns = [];
    this._rows = [];
    this._sort = null; // { id, dir: "asc"|"desc" }
    this._query = "";
    this._page = 1;

    this._onSearch = this._onSearch.bind(this);
  }

  connectedCallback() {
    this._parse();
    this._searchInput.addEventListener("input", this._onSearch);
    this._bindEvents();
    this._render();
  }

  disconnectedCallback() {
    this._searchInput.removeEventListener("input", this._onSearch);
  }

  /** Bind all interactive events once using delegation. */
  _bindEvents() {
    // Sort buttons in thead
    this._theadEl.addEventListener("click", (e) => {
      const btn = e.target.closest(".sort-btn");
      if (!btn) return;
      const colId = btn.dataset.col;
      if (this._sort && this._sort.id === colId) {
        this._sort = this._sort.dir === "asc" ? { id: colId, dir: "desc" } : null;
      } else {
        this._sort = { id: colId, dir: "asc" };
      }
      this._page = 1;
      this._renderHead();
      this._renderBody();
      this._renderFooter();
    });

    // Row clicks in tbody
    this._tbodyEl.addEventListener("click", (e) => {
      const tr = e.target.closest("tr[data-id]");
      if (!tr) return;
      const rowId = tr.dataset.id;
      const rowData = this._rows.find(r => String(r.id) === rowId);
      this.dispatchEvent(new CustomEvent("ctl-row-click", {
        bubbles: true, composed: true,
        detail: { id: rowId, row: rowData }
      }));
    });

    // Pagination buttons in footer
    this._footerEl.addEventListener("click", (e) => {
      const btn = e.target.closest(".page-btn");
      if (!btn || btn.hasAttribute("disabled")) return;
      const p = parseInt(btn.dataset.page, 10);
      const all = this._filteredRows();
      const pages = Math.ceil(all.length / this.pageSize);
      if (p >= 1 && p <= pages) {
        this._page = p;
        this._renderBody();
        this._renderFooter();
      }
    });
  }

  attributeChangedCallback(name) {
    if (name === "columns" || name === "rows") this._parse();
    if (name === "searchable") this._searchWrap.hidden = !this.searchable;
    this._page = 1;
    this._render();
  }

  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get searchable() { return this.hasAttribute("searchable"); }
  set searchable(v) { v ? this.setAttribute("searchable", "") : this.removeAttribute("searchable"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get pageSize() { return parseInt(this.getAttribute("page-size") || "10", 10); }
  set pageSize(v) { this.setAttribute("page-size", String(v)); }

  get columns() { return this._columns; }
  set columns(v) {
    this._columns = Array.isArray(v) ? v : [];
    this.setAttribute("columns", JSON.stringify(this._columns));
  }

  get rows() { return this._rows; }
  set rows(v) {
    this._rows = Array.isArray(v) ? v : [];
    this.setAttribute("rows", JSON.stringify(this._rows));
  }

  _parse() {
    const raw = this.getAttribute("columns");
    if (raw) this._columns = safeJsonParse(raw, []);
    const rraw = this.getAttribute("rows");
    if (rraw) this._rows = safeJsonParse(rraw, []);
  }

  _onSearch(e) {
    this._query = e.target.value;
    this._page = 1;
    this._renderBody();
    this._renderFooter();
  }

  _filteredRows() {
    let rows = [...this._rows];
    const q = this._query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(row =>
        Object.values(row).some(v => String(v ?? "").toLowerCase().includes(q))
      );
    }
    if (this._sort) {
      const { id, dir } = this._sort;
      rows.sort((a, b) => {
        const av = String(a[id] ?? "");
        const bv = String(b[id] ?? "");
        const numA = parseFloat(av.replace(/[^0-9.-]/g, ""));
        const numB = parseFloat(bv.replace(/[^0-9.-]/g, ""));
        const cmp = !isNaN(numA) && !isNaN(numB)
          ? numA - numB
          : av.localeCompare(bv);
        return dir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }

  _render() {
    this._titleEl.textContent = this.title;
    this._searchWrap.hidden = !this.searchable;
    if (this.loading) return;
    this._renderHead();
    this._renderBody();
    this._renderFooter();
  }

  _renderHead() {
    const cols = this._columns;
    if (cols.length === 0) return;
    this._theadEl.innerHTML = `<tr>${cols.map(col => {
      const align = col.align || "left";
      const label = this._esc(col.label || col.id || "");
      if (col.sortable) {
        const isSorted = this._sort && this._sort.id === col.id;
        const dir = isSorted ? this._sort.dir : null;
        const ariaSort = dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none";
        const arrow = dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅";
        return `<th class="${align}" scope="col">
          <button class="sort-btn" data-col="${this._esc(col.id)}" aria-sort="${ariaSort}">
            ${label} <span class="sort-icon" aria-hidden="true">${arrow}</span>
          </button>
        </th>`;
      }
      return `<th class="${align}" scope="col">${label}</th>`;
    }).join("")}</tr>`;
  }

  _renderBody() {
    const cols = this._columns;
    const all = this._filteredRows();
    const ps = this.pageSize;
    const start = (this._page - 1) * ps;
    const pageRows = all.slice(start, start + ps);
    const emptyLabel = this.getAttribute("empty-label") || "No results found";

    if (pageRows.length === 0) {
      this._tbodyEl.innerHTML = `<tr class="empty-row"><td colspan="${cols.length || 1}">${this._esc(emptyLabel)}</td></tr>`;
      return;
    }

    this._tbodyEl.innerHTML = pageRows.map(row => {
      const id = row.id || "";
      return `<tr data-id="${this._esc(String(id))}" role="row">${cols.map(col => {
        const val = row[col.id];
        const align = col.align || "left";
        let cellHtml = "";

        if (col.type === "badge") {
          const intent = row[`${col.id}_intent`] || inferIntent(String(val ?? ""));
          cellHtml = `<ctl-badge intent="${intent}">${this._esc(String(val ?? ""))}</ctl-badge>`;
        } else if (col.type === "avatar") {
          const initials = String(val ?? "").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
          const sub = row[`${col.id}_sub`] ? `<div class="subtext">${this._esc(String(row[`${col.id}_sub`]))}</div>` : "";
          cellHtml = `<div class="cell"><div class="avatar" aria-hidden="true">${this._esc(initials)}</div><div><div>${this._esc(String(val ?? ""))}</div>${sub}</div></div>`;
        } else if (col.type === "timestamp") {
          cellHtml = `<span title="${this._esc(String(val ?? ""))}">${this._esc(String(val ?? ""))}</span>`;
        } else {
          cellHtml = this._esc(String(val ?? ""));
        }

        return `<td class="${align}">${cellHtml}</td>`;
      }).join("")}</tr>`;
    }).join("");
  }

  _renderFooter() {
    const all = this._filteredRows();
    const ps = this.pageSize;
    const total = all.length;
    const pages = Math.ceil(total / ps);

    if (pages <= 1 && total === 0) { this._footerEl.innerHTML = ""; return; }

    const start = (this._page - 1) * ps + 1;
    const end = Math.min(this._page * ps, total);

    let pageBtns = "";
    if (pages > 1) {
      pageBtns = `<div class="pages" role="navigation" aria-label="Pagination">
        <button class="page-btn" data-page="${this._page - 1}" ${this._page <= 1 ? "disabled" : ""} aria-label="Previous page">‹</button>`;
      let lastWasEllipsis = false;
      for (let p = 1; p <= pages; p++) {
        const show = p === 1 || p === pages || Math.abs(p - this._page) <= 1;
        if (!show) {
          if (!lastWasEllipsis) {
            pageBtns += `<span style="padding:0 2px;color:var(--ctl-color-muted);">…</span>`;
            lastWasEllipsis = true;
          }
          continue;
        }
        lastWasEllipsis = false;
        pageBtns += `<button class="page-btn" data-page="${p}" ${p === this._page ? 'aria-current="page"' : ""} aria-label="Page ${p}">${p}</button>`;
      }
      pageBtns += `<button class="page-btn" data-page="${this._page + 1}" ${this._page >= pages ? "disabled" : ""} aria-label="Next page">›</button>
      </div>`;
    }

    this._footerEl.innerHTML = `
      <div class="count">${total > 0 ? `Showing ${start}–${end} of ${total}` : "No results"}</div>
      ${pageBtns}
    `;
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("ctl-activity-feed")) {
  customElements.define("ctl-activity-feed", CtlActivityFeed);
}
